/**
 * V2 Discover — the feed view. Newest yoinks in a card grid with
 * category-chip filtering, a Load More cursor, and (signed in) an
 * All / Following scope toggle. Following pages come from the
 * server-side-join RPC via the follows module (scale rule 3).
 */
import * as gateway from '../../core/gateway.js';
import { cached } from '../../core/query.js';
import { drainRows } from '../../core/drain.js';
import { yoinkCard, FEED_PAGE_SIZE } from '../../ui/card.js';
import { categoryChips } from '../../ui/chips.js';
import { fetchFollowedFeedPage, fetchFollowedMapPage } from '../follows/follows.js';
import {
  fetchAllCategories,
  filterStateParks,
  getShowStateParks,
  rankCategoryMatches,
  foldParkMatches,
} from '../../core/categories.js';
import { mountMap, mapsAvailable, getUserLocation, rowsInArea } from '../../ui/map-view.js';
import { setUrlParam, getUrlParam, shareButton, handleShareClick } from '../../ui/share.js';
import { slugify } from '../../../assets/js/shared/slugify.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { icon, categoryIcon } from '../../ui/icons.js';
import { startTicker } from '../community/ticker.js';
import { checklistHtml, markComplete, onProgressChange, hideChecklist, STEPS } from '../community/tutorial.js';
import { phoneCardHtml, dismissPhoneCard } from '../../ui/phone-card.js';
import { hasFeaturedLists, fetchMatchingLists } from '../lists/lists.js';
import { openLesson } from '../community/lessons.js';

// Split 2026-07-25: discover.js had grown to 843 lines against the plan's
// ~400 budget. Reads moved to ./discover-data.js, pure derivations and the
// segmented toggles to ./discover-derive.js. Every name this module exported
// before is re-exported below UNCHANGED — five test files import them from
// here, and the goal was file size, not a new interface.
import {
  fetchFeedPage, fetchPeople, fetchProfilesByIds, fetchMapPage,
} from './discover-data.js';
import {
  deriveCategories, sortFollowedFirst, cityBounds,
  viewToggle, scopeToggle,
} from './discover-derive.js';

export {
  fetchFeedPage, fetchPeople, fetchProfilesByIds, fetchMapPage,
  deriveCategories, sortFollowedFirst, cityBounds,
};

const CITY_ZOOM = 12;

/**
 * Mount the Discover view.
 * @param {HTMLElement} el - Container to render into
 * @param {object} ctx - { store, router }
 * @returns {() => void} cleanup
 */
// Category rows under a search: the fold row takes one of these slots.
const CAT_RESULT_LIMIT = 8;

export function mount(el, ctx) {
  const { store, router } = ctx;
  let yoinks = [];
  let cursor = null;
  let hasMore = true;
  let loading = false;
  let destroyed = false;
  let loadError = false;
  let allCats = [];
  let mapHandle = null;
  let collectedIds = null; // location ids the signed-in user has yoinked
  let peopleResults = []; // people matching the active search
  let catResults = []; // categories matching the active search
  let catFolded = 0; // park matches folded behind one row (see foldParkMatches)
  let listResults = []; // public lists matching the search (by title or category)
  let extrasQuery = ''; // the search the results above belong to
  // The follow panels (moved here from the journal's strip, user request
  // 2026-07-18). Open state lives outside render so re-renders (feed
  // pages settling, GPS re-scope) don't slam an open panel shut.
  let catsPanelOpen = false;
  let peoplePanelOpen = false;
  let followedPeople = null; // null = not fetched yet (lazy, on first open)
  let peopleLoading = false;
  // The Featured-lists door renders only when the shelf has something on
  // it (one cached id probe; fails soft pre-migration)
  let hasFeatured = false;
  hasFeaturedLists().then((any) => {
    if (destroyed || !any) return;
    hasFeatured = true;
    if (!loading && (store.get().viewMode || 'grid') === 'grid') render();
  });
  const stopTicker = startTicker({ router });
  // A shared link opens the way the sharer was looking (user rule
  // 2026-07-08): ?view=map, ?q= search, ?category= chip (slug, resolved
  // below once the category list loads).
  {
    const wanted = getUrlParam('view');
    if ((wanted === 'map' || wanted === 'grid') && wanted !== (store.get().viewMode || 'grid')) {
      store.set({ viewMode: wanted });
    }
    const q = getUrlParam('q');
    if (q && q !== (store.get().searchQuery || '')) store.set({ searchQuery: q });
  }
  // Tutorial ticks re-render the checklist — but never the map, whose
  // remount is exactly the blink loop (grid mode only)
  onProgressChange(() => {
    if (!destroyed && !loading && (store.get().viewMode || 'grid') === 'grid') render();
  });

  /**
   * V1's initializeLocationAsync (discovery.js:361-425): Discover is a
   * NEAR-YOU feed. First paint never blocks — the global feed renders,
   * then GPS resolves and the feed re-scopes to city bounds. One ask
   * per session; denial leaves the feed global (kinder than V1's
   * US-center fallback box for anyone outside the US).
   */
  function initFeedBounds() {
    const state = store.get();
    if (state.feedBounds || state.feedBoundsAsked) return;
    store.set({ feedBoundsAsked: true });
    // If the map already located us this session, scope to that at once —
    // no second GPS round-trip, no world-first flash on a Discover push.
    if (state.mapCenter) {
      store.set({ feedBounds: cityBounds(state.mapCenter) });
      load({ reset: true });
      return;
    }
    // The SAME fast, cache-friendly geolocation the map uses. The old ask
    // (enableHighAccuracy + maximumAge:0) forced a fresh high-accuracy fix
    // that routinely timed out on iPad and left the feed stuck on the whole
    // world, while the map — using a recent cached fix — zoomed right in.
    getUserLocation().then((center) => {
      if (destroyed || !center) return; // denied/unavailable — stay global
      store.set({ feedBounds: cityBounds(center), mapCenter: center, mapZoom: CITY_ZOOM });
      load({ reset: true });
    });
  }

  async function loadCollectedIds() {
    const user = store.get().user;
    if (!user) return;
    try {
      collectedIds = await cached(`my-locations:${user.id}`, async () => {
        // Drained past the old silent 1000-row cap (assessment 1.5): a
        // prolific explorer's map pins were quietly miscolored beyond it
        const rows = await drainRows(gateway.TABLES.YOINKS, {
          select: 'id,location_id',
          filters: { user_id: `eq.${user.id}` },
        });
        return new Set(rows.map((y) => y.location_id).filter(Boolean));
      });
    } catch {
      collectedIds = null;
    }
  }
  fetchAllCategories().then((cats) => {
    allCats = cats;
    // A shared ?category= slug becomes the chip filter once the list is
    // here to resolve it against (profile pages use the same param).
    const slug = getUrlParam('category');
    if (slug && !store.get().categoryFilter) {
      const match = cats.find((c) => slugify(c.name) === slug);
      if (match) {
        store.set({ categoryFilter: match.id });
        load({ reset: true });
      }
    }
  }).catch(() => {});

  /**
   * People + category + list matches for the submitted search. Runs
   * alongside the feed page, re-rendering when it settles (unless a newer
   * search or the feed's own render will cover it). Lists match on their
   * own title/description AND on carrying a matched category — searching
   * "Burger" surfaces "Top 16 Burgers in Los Angeles" because the list
   * carries the Burgers category, not because its title cooperates.
   */
  async function loadExtras(q) {
    try {
      const cats = await fetchAllCategories();
      // Ranked, park flood folded (2026-09-15): "Park" itself leads the
      // "park" search instead of eight state parks from the top of the
      // alphabet. Lists match on the top-ranked categories (as before —
      // never the whole flood: 1,500 ids in one in.(…) filter is a URL
      // the gateway would refuse, and that failure would take the People
      // and Categories groups down with it).
      const ranked = rankCategoryMatches(cats, q, cats, { description: true });
      const { rows, folded } = foldParkMatches(ranked, { limit: CAT_RESULT_LIMIT, showParks: getShowStateParks() });
      const topIds = [...ranked.others, ...ranked.parks].slice(0, CAT_RESULT_LIMIT).map((c) => c.id);
      const [people, lists] = await Promise.all([
        fetchPeople(q),
        fetchMatchingLists(q, topIds),
      ]);
      if (destroyed || q !== extrasQuery) return;
      peopleResults = people;
      catResults = rows;
      catFolded = folded;
      listResults = lists;
    } catch {
      if (destroyed || q !== extrasQuery) return;
      peopleResults = [];
      catResults = [];
      catFolded = 0;
      listResults = [];
    }
    if (!loading) render();
  }

  function personRow(p, followed) {
    const name = p.full_name || p.username || '';
    const avatar = p.avatar_url
      ? `<img class="friend-avatar" src="${gateway.getPublicUrl('avatars', p.avatar_url)}" alt="">`
      : `<span class="friend-avatar friend-avatar-blank" aria-hidden="true">${escapeHtml(name.charAt(0).toUpperCase())}</span>`;
    return `
      <button type="button" class="v2-result-row" data-username="${escapeHtml(p.username)}">
        ${avatar}
        <span class="result-name">${escapeHtml(name)}</span>
        ${p.full_name && p.full_name !== p.username ? `<span class="result-sub">@${escapeHtml(p.username)}</span>` : ''}
        ${followed ? '<span class="result-badge">Following</span>' : ''}
      </button>`;
  }

  function categoryRow(cat) {
    return `
      <button type="button" class="v2-result-row" data-category-slug="${slugify(cat.name)}">
        ${categoryIcon(cat, 18)}
        <span class="result-name">${escapeHtml(cat.name)}</span>
      </button>`;
  }

  function listResultRow(l) {
    const who = l.owner.full_name || l.owner.username;
    return `
      <button type="button" class="v2-result-row"
        data-result-list-owner="${escapeHtml(l.owner.username)}" data-result-list-slug="${escapeHtml(l.slug)}">
        ${icon('bookmark', 18)}
        <span class="result-name">${l.featured ? '<span class="featured-star" aria-hidden="true">★</span> ' : ''}${escapeHtml(l.title)}</span>
        <span class="result-sub">by ${escapeHtml(who)} · ${l.entry_count} place${l.entry_count === 1 ? '' : 's'}</span>
      </button>`;
  }

  /** The People / Categories groups above the yoink grid, search only. */
  function searchResultsHtml() {
    if (!(store.get().searchQuery || '').trim()) return '';
    const followed = new Set(store.get().followingIds || []);
    const people = sortFollowedFirst(peopleResults, [...followed]);
    const sections = [];
    if (people.length) {
      sections.push(`
        <section class="v2-result-group" aria-label="Matching people">
          <h3 class="v2-results-title">People</h3>
          <div class="v2-result-rows">${people.map((p) => personRow(p, followed.has(p.id))).join('')}</div>
        </section>`);
    }
    if (catResults.length || catFolded) {
      // The fold row is a door to the category browser with the parks
      // toggle on for that visit — a real page with a real toggle beats
      // unfolding a thousand rows into an 8-slot list.
      const foldRow = catFolded
        ? `<button type="button" class="v2-result-row v2-parks-fold" data-browse-parks="${escapeHtml(extrasQuery)}">
            <span class="result-fold-mark" aria-hidden="true">▸</span>
            <span class="result-name">${catFolded.toLocaleString()} parks &amp; protected areas also match</span>
            <span class="result-sub">browse them</span>
          </button>`
        : '';
      sections.push(`
        <section class="v2-result-group" aria-label="Matching categories">
          <h3 class="v2-results-title">Categories</h3>
          <div class="v2-result-rows">${catResults.map(categoryRow).join('')}${foldRow}</div>
        </section>`);
    }
    if (listResults.length) {
      sections.push(`
        <section class="v2-result-group" aria-label="Matching lists">
          <h3 class="v2-results-title">Lists</h3>
          <div class="v2-result-rows">${listResults.map(listResultRow).join('')}</div>
        </section>`);
    }
    if (!sections.length) return '';
    return `<div class="v2-search-results">${sections.join('')}</div>`;
  }

  /**
   * The Explore tray: your follows' entry points (signed in only) and the
   * discovery doors, behind one toolbar button. Chips jump to category
   * pages, rows to journals; the Following scope toggle stays the feed
   * filter — these are navigation, which is why they belong together and
   * away from the filters rather than stacked between you and the cards.
   */
  function exploreTrayHtml() {
    const { user, followedCategoryIds, followingIds } = store.get();
    const catIds = new Set(followedCategoryIds || []);
    const followedCats = allCats.filter((c) => catIds.has(c.id));
    const peopleCount = (followingIds || []).length;
    const catBody = followedCats.length
      ? `<div class="v2-chips">
          ${followedCats.map((c) => `
            <button class="v2-chip" data-category-slug="${slugify(c.name)}">
              ${categoryIcon(c, 16)}<span>${escapeHtml(c.name)}</span>
            </button>`).join('')}
        </div>`
      : catIds.size
        ? '<p class="log-hint">Unrolling the map…</p>'
        : '<p class="log-hint">Open a category and tap Follow — it’ll show up here.</p>';
    const peopleBody = !peopleCount
      ? '<p class="log-hint">Visit an explorer’s journal and tap Follow — they’ll show up here.</p>'
      : followedPeople === null
        ? '<p class="log-hint">Unrolling the map…</p>'
        : `<div class="v2-result-rows">${followedPeople.map((p) => personRow(p, false)).join('')}</div>`;
    return `
      <div class="v2-explore-tray v2-tool-group${openTool === 'explore' ? ' is-open' : ''}"
        id="tool-explore" data-tool-group="explore">
        ${user ? `
        <div class="v2-follow-panels${catsPanelOpen || peoplePanelOpen ? ' has-open' : ''}">
          <details data-follow-panel="cats" ${catsPanelOpen ? 'open' : ''}>
            <summary class="v2-btn-more">${icon('tag', 16)}
              <span class="follow-long">Categories you follow</span><span class="follow-short">Categories</span>
              (${catIds.size})</summary>
            ${catBody}
          </details>
          <details data-follow-panel="people" ${peoplePanelOpen ? 'open' : ''}>
            <summary class="v2-btn-more">${icon('person', 16)}
              <span class="follow-long">People you follow</span><span class="follow-short">People</span>
              (${peopleCount})</summary>
            ${peopleBody}
          </details>
        </div>` : ''}
        ${doorsHtml()}
      </div>`;
  }

  async function loadFollowedPeople() {
    const ids = store.get().followingIds || [];
    if (peopleLoading || !ids.length) return;
    peopleLoading = true;
    try {
      const rows = await fetchProfilesByIds(ids);
      if (destroyed) return;
      followedPeople = rows;
    } catch {
      if (destroyed) return;
      followedPeople = []; // the count still shows; rows just stay away
    }
    peopleLoading = false;
    if (!loading) render();
  }

  /**
   * The empty state — and, since the standing area line went away, the
   * one place the near-you box is ever named: "only need an alert if
   * there is literally nothing in the current map zoom" (2026-08-06).
   * The way out is a button in the sentence rather than a pointer at a
   * control somewhere above, which is what it used to be.
   */
  function emptyLine(scope) {
    if (scope === 'following') {
      return 'Nothing from your followed categories or explorers yet. Follow the categories you love and new finds land here.';
    }
    return store.get().feedBounds
      ? 'Nothing yoinked in this part of the map yet — '
        + '<button type="button" class="v2-search-clear" data-clear-bounds>show everywhere</button>'
        + ', or be the first to log one.'
      : 'Nothing yoinked yet.';
  }

  /** Where the feed is looking, and the way out of it. */
  /**
   * The phone toolbar: one row of icons, each opening ONE row of tools
   * below it. A button lights when its group is holding a non-default
   * state, because a closed tray must never hide the fact that the feed is
   * filtered — that is the whole risk of this shape, so the state comes
   * back out onto the icon (the active category by name, "Following",
   * "Map", the live query).
   */
  function toolbarHtml(scope, categoryFilter, { mapMode = false } = {}) {
    const query = (store.get().searchQuery || '').trim();
    const cat = categoryFilter
      && allCats.find((c) => c.id === categoryFilter)?.name;
    const tools = [
      // On the map the View band stays out in the open — hiding the way off
      // a map behind an icon is how you strand someone — so the tool that
      // would open it is the one thing the map's toolbar drops. The rest
      // come along: the map honors the category and the query exactly as
      // the grid does (loadMap), and until 2026-08-06 neither was reachable
      // from it ("the grid/map toggle should not remove all the navigation").
      ...(mapMode ? [] : [{
        id: 'view',
        icon: 'grid',
        label: 'View',
        // The label still says which feed you are on — a shut tray must
        // never hide that — but the red ring is the stronger claim, and
        // the hazard it was built for is a NARROWED feed that looks
        // unfiltered. All is the widest feed, so it never rings; and
        // Following stopped ringing on 2026-08-06 when it became the
        // default (user, same day: "why the red dot outline?"). What is
        // left is the one reader it still helps: someone who follows
        // nothing and picked Following by hand, whose feed is empty for
        // exactly that reason and who would otherwise never know why.
        state: scope === 'following' ? 'Following' : '',
        alarm: scope === 'following' && defaultScope() !== 'following',
      }]),
      { id: 'search', icon: 'search', label: 'Search', state: query },
      { id: 'filter', icon: 'tag', label: 'Filter', state: cat || '' },
      // Follows and the two discovery doors are the same KIND of thing —
      // navigation, not feed filters — so they share one tray (2026-08-06
      // user ask: fold Explore into the toolbar buttons). Unconditional,
      // unlike the follow panels it used to be: signed out the tray is the
      // doors alone, and those are exactly what a stranger wants.
      { id: 'explore', icon: 'road', label: 'Explore', state: '' },
    ];
    return `
      <div class="v2-toolbar" role="toolbar" aria-label="Feed tools">
        ${tools.map((t) => `
          <button type="button" class="v2-tool-btn${openTool === t.id ? ' is-open' : ''}${
            (t.alarm ?? Boolean(t.state)) ? ' has-state' : ''}"
            data-tool="${t.id}" aria-expanded="${openTool === t.id}" aria-controls="tool-${t.id}">
            ${icon(t.icon, 20)}
            <span class="tool-label">${escapeHtml(t.state || t.label)}</span>
            ${t.state ? `<span class="visually-hidden">${escapeHtml(t.label)} is set to</span>` : ''}
          </button>`).join('')}
      </div>`;
  }

  /**
   * The Search tray. Extracted 2026-08-06 so the map renders the SAME
   * band the grid does rather than a copy that drifts — the map has
   * honored the query all along (loadMap), it just had no box to type in.
   */
  function searchFormHtml() {
    const q = store.get().searchQuery || '';
    return `
      <form class="v2-search v2-tool-group${openTool === 'search' ? ' is-open' : ''}"
        id="tool-search" data-tool-group="search" data-search-form>
        <label class="visually-hidden" for="v2-search-input">Search yoinks, categories, and people</label>
        <input id="v2-search-input" type="search" name="feedSearch" placeholder="Search yoinks, categories, people…"
          autocomplete="off" enterkeyhint="search"
          value="${q.replace(/"/g, '&quot;')}">
        <button type="submit" class="v2-search-go"><span class="search-go-word">Search</span></button>
        ${q ? '<button type="button" class="v2-search-clear" data-search-clear>Clear</button>' : ''}
      </form>`;
  }

  /** The Filter tray — the category chips. Shared with the map (same reason). */
  function chipRowHtml(categoryFilter) {
    return `
      <div class="v2-chip-row v2-tool-group${openTool === 'filter' ? ' is-open' : ''}"
        id="tool-filter" data-tool-group="filter">
        ${categoryChips(
          filterStateParks(deriveCategories(yoinks, store.get().profile?.role === 'admin'), allCats, getShowStateParks()),
          categoryFilter,
          store.get().followedCategoryIds || [],
          '<button class="v2-chip v2-chip-browse" data-browse-cats>All categories →</button>'
        )}
      </div>`;
  }

  /**
   * The feed's scope, resolved. Following is the default for anyone who
   * actually follows something (2026-08-06 user ask) — it is the feed
   * they built, and the reason they followed anything. Someone who
   * follows nothing would get an empty page out of it, so they stay on
   * All until their first follow; nobody is worse off either way.
   *
   * A null feedScope means "not chosen yet". Tapping either button writes
   * a real value, and from then on this returns the reader's choice —
   * their pick must not be re-decided when a follow lands.
   */
  function effectiveScope() {
    const { user, feedScope } = store.get();
    if (!user) return 'all';
    return feedScope || defaultScope();
  }

  /**
   * What this reader gets WITHOUT choosing — the same rule effectiveScope
   * falls back on, split out because the toolbar needs to know whether the
   * scope in force is a deviation or simply the default.
   */
  function defaultScope() {
    const { user, followedCategoryIds, followingIds } = store.get();
    if (!user) return 'all';
    const follows = (followedCategoryIds?.length || 0) + (followingIds?.length || 0);
    return follows ? 'following' : 'all';
  }

  /**
   * The way out of the near-you box, and the only trace of it left in the
   * chrome. A standing "Showing finds near you · Show everywhere" line sat
   * above every feed until 2026-08-06 ("perhaps we can lose that line"):
   * it announced a filter the cards under it were already demonstrating,
   * and it cost a band on every single visit to say so.
   *
   * The button survives because losing it outright would strand anyone
   * wanting to browse past their own city — every signed-in visit gets
   * GPS bounds. It rides in the View tray, where the feed's other scope
   * controls live, and appears only while a box is actually on.
   */
  function everywhereButtonHtml(scope) {
    if (scope !== 'all' || !store.get().feedBounds) return '';
    // NOT .v2-btn-share, however much it looks like one: handleShareClick
    // claims that class from the same delegated handler and would swallow
    // the tap. Styled beside it (components.css), wired to nothing else.
    return `<button type="button" class="v2-btn-everywhere" data-clear-bounds>
      ${icon('globe', 16)}<span>Everywhere</span></button>`;
  }

  /**
   * The discovery doors. They spent 2026-08-04 under the search box behind
   * their own "Explore" pulldown, because open they crowded a laptop. The
   * toolbar's Explore tray IS that pulldown now, so the <details> is gone
   * and the doors are a plain row again — one fewer band on the page, and
   * one fewer thing to expand before you reach what you came for.
   */
  function doorsHtml() {
    const doors = [
      `<button type="button" class="v2-route-door" data-open-route>
          ${icon('road', 16)}Driving somewhere? See what’s on the way</button>`,
    ];
    if (hasFeatured) {
      doors.push(`<button type="button" class="v2-route-door" data-open-featured>
          ${icon('bookmark', 16)}<span class="featured-star" aria-hidden="true">★</span> Featured lists — worth borrowing</button>`);
    }
    return `<div class="v2-door-row">${doors.join('')}</div>`;
  }

  function render() {
    const { categoryFilter, user } = store.get();
    const scope = effectiveScope();
    if (loadError) {
      el.innerHTML = `
        ${user ? scopeToggle(scope) : ''}
        <div class="v2-error">Couldn’t reach the journal. <button class="v2-btn-more">Retry</button></div>`;
      return;
    }
    const viewMode = store.get().viewMode || 'grid';
    if (viewMode === 'map') {
      // The map used to render a bare Grid/Map row and nothing else, so
      // switching to it dropped every other way to steer the feed — even
      // though loadMap honors the category and the query. Same toolbar,
      // same trays; only the View tool is missing, because the band it
      // would open is out in the open here (see toolbarHtml).
      el.innerHTML = `
        <div class="discover-body">
        <h2 class="visually-hidden">Discover recent yoinks on the map</h2>
        ${toolbarHtml(scope, categoryFilter, { mapMode: true })}
        <div class="discover-controls">
          <div class="v2-toggles">${user ? scopeToggle(scope) : ''}${viewToggle(viewMode)}${everywhereButtonHtml(scope)}${shareButton('Discover places to visit on Yoink Adventures')}</div>
          ${searchFormHtml()}
        </div>
        ${checklistHtml(!!user)}
        ${chipRowHtml(categoryFilter)}
        ${exploreTrayHtml()}
        <div class="v2-map" data-map-host></div>
        <p class="log-hint">The map shows what’s in view — pan and zoom to explore.</p>
        </div>`;
      mapHandle?.cleanup();
      mapHandle = mountMap(el.querySelector('[data-map-host]'), {
        store,
        router,
        onBoundsChange: (bounds) => loadMap(bounds),
      });
      // No eager global load: it painted 1000 clustered markers only for
      // the first idle to wipe them (the big-brown/small-orange blink).
      // The map reports its viewport through onBoundsChange right after
      // mount; this fallback covers the mocked-Maps case only.
      if (mapsAvailable()) {
        setTimeout(() => {
          if (!destroyed && !mapLoadedOnce && (store.get().viewMode || 'grid') === 'map') {
            loadMap(store.get().feedBounds || null);
          }
        }, 1500);
      }
      return;
    }
    mapHandle?.cleanup();
    mapHandle = null;
    // Wrapped so the phone layout can REORDER these bands rather than just
    // shrink them (2026-07-27, second pass: "still pretty cluttered" — the
    // follow panels are navigation, not filters, so on a phone they belong
    // under the cards, not between the visitor and the cards).
    el.innerHTML = `
      <div class="discover-body">
      <h2 class="visually-hidden">Discover recent yoinks</h2>
      ${toolbarHtml(scope, categoryFilter)}
      <div class="discover-controls">
        <div class="v2-toggles v2-tool-group${openTool === 'view' ? ' is-open' : ''}"
          id="tool-view" data-tool-group="view">${user ? scopeToggle(scope) : ''}${viewToggle(viewMode)}
          ${everywhereButtonHtml(scope)}
          ${shareButton('Discover places to visit on Yoink Adventures')}</div>
        ${searchFormHtml()}
      </div>
      ${searchResultsHtml()}
      ${checklistHtml(!!store.get().user)}
      ${phoneCardHtml(store.get())}
      ${chipRowHtml(categoryFilter)}
      ${exploreTrayHtml()}
      ${(store.get().searchQuery || '').trim() ? '<h3 class="v2-results-title">Yoinks</h3>' : ''}
      <div class="v2-grid">${yoinks.map(yoinkCard).join('')}</div>
      <div class="v2-feed-foot" aria-live="polite">
        ${loading ? '<div class="v2-loading">Unrolling the map…</div>' : ''}
        ${!loading && hasMore && yoinks.length ? '<button class="v2-btn-more">Load more</button>' : ''}
        ${!loading && !yoinks.length ? `<div class="v2-empty">${emptyLine(scope)}</div>` : ''}
      </div>
      </div>
    `;
  }

  // Phone toolbar (2026-07-27, fifth pass — the user's shape: "one top row
  // with icons and then each click on an icon opens just one simple row
  // below with the tools for each icon"). One tool open at a time, none by
  // default, so the feed opens as one band of icons and then cards.
  // Desktop ignores all of this: the toolbar is display:none there and
  // every group shows at once, exactly as before.
  //
  // 'search' opens itself when a query is already in play — a shared ?q=
  // link that hid its own input would read as a broken feed.
  let openTool = (store.get().searchQuery || '').trim() ? 'search' : null;

  let mapLoadedOnce = false;

  function sameBounds(a, b) {
    if (!a || !b) return false;
    return ['north', 'south', 'east', 'west'].every((k) => Math.abs(a[k] - b[k]) < 1e-7);
  }

  async function loadMap(bounds) {
    mapLoadedOnce = true;
    // The viewport IS the filter, and it carries back to the cards:
    // V1 kept map bounds in shared state across the grid<->map toggle
    // (map.js:140-162, destroyMap keeps them; discovery.js:1089-1094).
    // Write-on-change only — store.set notifies unconditionally.
    if (bounds && !sameBounds(store.get().feedBounds, bounds)) {
      store.set({ feedBounds: { ...bounds, source: 'map' } });
    }
    try {
      const { user, categoryFilter, searchQuery } = store.get();
      const scope = effectiveScope();
      if (user && !collectedIds) await loadCollectedIds();
      // The map honors the scope like the grid does (V1: both views drew
      // from the same filtered load) — in Following it's the followed
      // feed, viewport-filtered client-side since the RPC has no bounds
      const rows =
        scope === 'following'
          ? rowsInArea(
              await fetchFollowedMapPage({
                categoryId: categoryFilter,
                search: searchQuery || '',
              }),
              bounds
            )
          : await fetchMapPage({
              categoryId: categoryFilter,
              search: searchQuery || '',
              bounds,
            });
      if (destroyed) return;
      mapHandle?.setYoinks(rows, store.get().user ? collectedIds : null);
    } catch {
      // Map load failures are non-fatal; the canvas simply stays as-is
    }
  }

  let queuedReset = false;
  async function load({ reset = false } = {}) {
    if (loading) {
      // A re-scope requested mid-load (e.g. GPS resolving from cache while
      // the first global page is still in flight) must not be dropped, or
      // the feed stays on the whole world. Run it once this load settles.
      if (reset) queuedReset = true;
      return;
    }
    loading = true;
    loadError = false;
    if (reset) {
      cursor = null;
      yoinks = [];
      hasMore = true;
      // People/category matches track the submitted search (a stale set
      // must not linger under a new query, or after Clear)
      const q = (store.get().searchQuery || '').trim();
      if (q !== extrasQuery) {
        peopleResults = [];
        catResults = [];
        catFolded = 0;
        listResults = [];
        extrasQuery = q;
      }
      if (q) loadExtras(q);
    }
    render();
    const { categoryFilter } = store.get();
    const scope = effectiveScope();
    try {
      const batch =
        scope === 'following'
          ? await fetchFollowedFeedPage({
              cursor,
              categoryId: categoryFilter,
              search: store.get().searchQuery || '',
            })
          : await fetchFeedPage({
              categoryId: categoryFilter,
              cursor,
              search: store.get().searchQuery || '',
              bounds: store.get().feedBounds || null,
            });
      if (destroyed) return;
      yoinks = cursor === null ? batch : [...yoinks, ...batch];
      hasMore = batch.length === FEED_PAGE_SIZE;
      const last = yoinks[yoinks.length - 1];
      cursor = last ? { created_at: last.created_at, id: last.id } : null;
    } catch {
      if (destroyed) return;
      loading = false;
      loadError = true;
      render();
      if (queuedReset) { queuedReset = false; load({ reset: true }); }
      return;
    }
    loading = false;
    render();
    if (queuedReset) { queuedReset = false; load({ reset: true }); }
  }

  function onClick(event) {
    if (handleShareClick(event)) return;
    if (event.target.closest('[data-phone-card-open]')) {
      router.navigate('phone');
      return;
    }
    if (event.target.closest('[data-phone-card-dismiss]')) {
      dismissPhoneCard();
      render();
      return;
    }
    if (event.target.closest('[data-tut-close]')) {
      event.preventDefault(); // don't let the details toggle underneath
      hideChecklist();
      return;
    }
    if (event.target.closest('.tutorial-card summary')) {
      markComplete('open_how_it_works');
      // let the native details toggle proceed
    }
    const tutStep = event.target.closest('[data-tut-step]');
    if (tutStep) {
      openLesson(tutStep.dataset.tutStep, {
        signedIn: !!store.get().user,
        viewMode: store.get().viewMode || 'grid',
        label: STEPS.find((s) => s.id === tutStep.dataset.tutStep)?.label,
      });
      return;
    }
    const viewBtn = event.target.closest('[data-view-mode]');
    if (viewBtn) {
      // Complete only on a REAL switch — tapping the already-active side
      // changes nothing (and it's the side the lesson never pulses)
      if (viewBtn.dataset.viewMode !== (store.get().viewMode || 'grid')) {
        markComplete('switch_view');
        store.set({ viewMode: viewBtn.dataset.viewMode });
        setUrlParam('view', viewBtn.dataset.viewMode === 'map' ? 'map' : '');
        if (viewBtn.dataset.viewMode === 'grid') {
          // Coming back from the map: the cards re-query within the
          // viewport you just explored (V1 behavior, M8.5 Tier 1)
          load({ reset: true });
        } else {
          render();
        }
      }
      return;
    }
    const scopeBtn = event.target.closest('[data-feed-scope]');
    if (scopeBtn) {
      const scope = scopeBtn.dataset.feedScope;
      // Against the RESOLVED scope, not the stored one: with no choice
      // made yet the stored value is null, and comparing to that would
      // "switch" to the side already showing.
      if (scope !== effectiveScope()) {
        markComplete('switch_scope');
        // Switching scope also clears the category chip, V1's exact rule
        // (app.js:639,649) — the chip you picked in one scope rarely
        // exists in the other, and a stale invisible filter reads as
        // "the feed is broken"
        store.set({ feedScope: scope, categoryFilter: null });
        lastScope = scope;
        setUrlParam('category', '');
        load({ reset: true });
      }
      return;
    }
    if (event.target.closest('[data-browse-cats]')) {
      router.navigate('categories');
      return;
    }
    if (event.target.closest('[data-open-route]')) {
      router.navigate('route');
      return;
    }
    if (event.target.closest('[data-open-featured]')) {
      router.navigate('featured-lists');
      return;
    }
    const panelSummary = event.target.closest('[data-follow-panel] > summary');
    if (panelSummary) {
      // Mirror the native toggle (which proceeds after this click) into
      // local state; the people list fetches on its first open only.
      const panel = panelSummary.parentElement;
      const opening = !panel.open;
      if (panel.dataset.followPanel === 'people') {
        peoplePanelOpen = opening;
        if (opening && followedPeople === null) loadFollowedPeople();
      } else {
        catsPanelOpen = opening;
      }
      return;
    }
    const tool = event.target.closest('[data-tool]');
    if (tool) {
      const id = tool.dataset.tool;
      openTool = openTool === id ? null : id; // tapping the open one closes it
      render();
      if (openTool === 'search') el.querySelector('#v2-search-input')?.focus();
      return;
    }
    if (event.target.closest('[data-search-clear]')) {
      store.set({ searchQuery: '' });
      setUrlParam('q', '');
      load({ reset: true });
      return;
    }
    if (event.target.closest('[data-clear-bounds]')) {
      store.set({ feedBounds: null });
      load({ reset: true });
      return;
    }
    const chip = event.target.closest('[data-category-id]');
    if (chip && chip.classList.contains('v2-chip')) {
      const id = chip.dataset.categoryId || null;
      if (id && id === store.get().categoryFilter && chip.dataset.categoryNav) {
        // V1 discovery.js:600-604: the selected chip's second tap opens
        // that category's page
        router.navigate('category', { slug: chip.dataset.categoryNav });
        return;
      }
      store.set({ categoryFilter: id });
      setUrlParam('category', id ? chip.dataset.categoryNav || '' : '');
      load({ reset: true });
      return;
    }
    if (event.target.closest('.v2-btn-more')) {
      load();
      return;
    }
    const listRowEl = event.target.closest('[data-result-list-owner]');
    if (listRowEl) {
      router.navigate('list', {
        username: listRowEl.dataset.resultListOwner,
        slug: listRowEl.dataset.resultListSlug,
      });
      return;
    }
    const personalRow = event.target.closest('[data-personal-slug]');
    if (personalRow) {
      router.navigate('personal-category', {
        username: personalRow.dataset.personalOwner, slug: personalRow.dataset.personalSlug,
      });
      return;
    }
    const catRow = event.target.closest('[data-category-slug]');
    if (catRow) {
      router.navigate('category', { slug: catRow.dataset.categorySlug });
      return;
    }
    const browseParks = event.target.closest('[data-browse-parks]');
    if (browseParks) {
      router.navigate('categories', {}, { query: { q: browseParks.dataset.browseParks, parks: '1' } });
      return;
    }
    const userRow = event.target.closest('[data-username]');
    if (userRow && userRow.dataset.username) {
      router.navigate('user', { username: userRow.dataset.username });
      return;
    }
    const locationRow = event.target.closest('[data-location-id]');
    if (locationRow) {
      router.navigate('location', { id: locationRow.dataset.locationId });
      return;
    }
    const card = event.target.closest('[data-yoink-id]');
    if (card) {
      router.navigate('yoink', { id: card.dataset.yoinkId });
    }
  }

  // Keyboard activation for the role=button/link elements (cards, place
  // links): Enter and Space must do what click does — WCAG 2.1.1.
  function onKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target.closest('[data-yoink-id], [data-location-id], [data-username], [data-category-slug], [data-personal-slug]');
    if (!target || target.tagName === 'BUTTON') return;
    event.preventDefault();
    onClick(event);
  }

  // React to auth changes mid-view: the toggle appears on login; a
  // sign-out while in Following scope falls back to the All feed.
  let lastUserId = store.get().user?.id || null;
  let lastFollowCounts = `${(store.get().followedCategoryIds || []).length}:${(store.get().followingIds || []).length}`;
  // What effectiveScope() last resolved to. The subscriber runs AFTER the
  // store has changed, so this is the only way to tell a follow that
  // merely moved a count from one that flipped the whole feed.
  let lastScope = effectiveScope();
  const unsubscribe = store.subscribe((state) => {
    const userId = state.user?.id || null;
    const followCounts = `${(state.followedCategoryIds || []).length}:${(state.followingIds || []).length}`;
    if (userId === lastUserId) {
      // The follow slices settle just after login (main.js loadFollowing)
      // — the panel counts must catch that. Grid mode only: a map render
      // here would remount it (the 2026-07-07 blink loop).
      if (followCounts !== lastFollowCounts) {
        lastFollowCounts = followCounts;
        // The follow slices land a beat after login, and the FIRST one
        // flips an undecided reader from All to Following — a different
        // feed, not a different caption, so it reloads rather than
        // repaints (2026-08-06 default).
        const scopeNow = effectiveScope();
        if (scopeNow !== lastScope) {
          lastScope = scopeNow;
          load({ reset: true });
        } else if (!loading && (state.viewMode || 'grid') === 'grid') {
          render();
        }
      }
      return;
    }
    lastUserId = userId;
    lastFollowCounts = followCounts;
    lastScope = effectiveScope();
    if (!userId && state.feedScope) {
      store.set({ feedScope: null }); // signed out there is only one feed
      load({ reset: true });
    } else {
      render();
    }
  });

  // Search runs only on submit (the Search button or the keyboard's
  // Search/Enter key) — no per-keystroke queries, no focus loss on
  // re-render (user feedback 2026-07-03).
  function onSubmit(event) {
    const form = event.target.closest('[data-search-form]');
    if (!form) return;
    event.preventDefault();
    const value = form.elements.feedSearch.value.trim();
    if (value === (store.get().searchQuery || '')) return;
    store.set({ searchQuery: value });
    setUrlParam('q', value);
    load({ reset: true });
  }

  // Infinite scroll (V1 discovery.js:880-905): within 300px of the
  // bottom auto-loads the next page; the Load More button stays as the
  // fallback for keyboard/reader users.
  let scrollTimer = null;
  function onScroll() {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      if (destroyed || loading || !hasMore || (store.get().viewMode || 'grid') !== 'grid') return;
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 300;
      if (nearBottom && yoinks.length) load();
    }, 200);
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKeydown);
  el.addEventListener('submit', onSubmit);
  load({ reset: true });
  initFeedBounds();

  return () => {
    destroyed = true;
    stopTicker();
    clearTimeout(scrollTimer);
    window.removeEventListener('scroll', onScroll);
    mapHandle?.cleanup();
    unsubscribe();
    el.removeEventListener('click', onClick);
    el.removeEventListener('keydown', onKeydown);
    el.removeEventListener('submit', onSubmit);
  };
}
